import { BehaviorInfoMinimal, BlueprintMinimalInfo } from "./Blueprints";

export interface CompaniesApi {
  external_id: string;
  is_active: boolean;
  is_deleted: boolean;
  name: string;
  notes: string;
  banner_notes?: string;
  is_child?: boolean;
  review: boolean;
  callback_config?: any;
  template?: string;
  template_definition?: any;
  chat_support: boolean;
  instant_fit_subscriber: boolean;
  consent_forms: boolean;
  client_qa?: boolean;
  internal_qa?: boolean;
  sso_domain?: string;
  cqa_autocomplete_days?: number;
  default_behavior_kit?: string;
  default_blueprint?: string;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
  report_analytics_charts?: boolean;
  multilanguage?: boolean;
  lens_topics?: boolean;
  show_person_info_on_pdf?: boolean;
  callback_on_amendment_complete?: boolean;
}

export interface ReportAnalyticsChartsResponse {
  report_analytics_charts: boolean;
}

export interface CompanySettingTypes {
  company_setting_uuid: string;
  setting_type_uuid: string;
  value: string;
  name: string;
  data_type: "INT" | "STRING" | "BOOL";
}

export interface CompanyFilterInfoApi {
  company_uuid?: string;
  parent_company_uuid?: string;
  name: string;
  blueprint: string[];
  workflow?: string[];
}

export interface CompanyInfoApi {
  company_uuid?: string;
  name: string;
  blueprints: string[];
  company_settings: CompanySettingTypes[];
  data_type?: string;
  review: boolean;
  template?: string;
  chat_support: boolean;
  instant_fit_subscriber: boolean;
  consent_forms: boolean;
  client_qa?: boolean;
  internal_qa?: boolean;
  cqa_autocomplete_days?: number;
  default_behavior_kit?: string;
  default_blueprint?: string;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
  multilanguage?: boolean;
  lens_topics?: boolean;
}

export interface CompanyDetail {
  callback_config: any;
  name: string;
  notes: string;
  isChild: boolean;
  settings: CompanySettingTypes[];
  loaded: boolean;
  external_id: string;
  review: boolean;
  template?: string;
  chat_support: boolean;
  instant_fit_subscriber: boolean;
  consent_forms: boolean;
  pdfTemplate?: PDFTemplate;
  client_qa?: boolean;
  internal_qa?: boolean;
  cqa_autocomplete_days?: number;
  default_behavior_kit?: string;
  default_behavior_kit_assigned_by?: string;
  default_behavior_kit_assigned_by_name?: string;
  default_blueprint?: string;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
  multilanguage?: boolean;
  lens_topics?: boolean;
}

export interface CompanyListInputApi {
  page: number;
  size: number;
  company_ids?: string[];
  search?: string;
}

export interface SubCompanyListInputApi {
  page: number;
  size: number;
  company_uuid: string;
  search?: string;
}

export interface SubCompanySearchListInputApi {
  page: number;
  size: number;
  company_uuid: string;
}

export interface CompaniesWithFilter {
  items: CompanyFilterInfoApi[];
  page: number;
  total: number;
  size: number;
}

export interface CompanyCreationSetting {
  setting_type_uuid: string;
  value: unknown;
}

export interface CreateCompanyInput {
  company: {
    modified_by?: string;
    external_id?: string;
    parent_company_uuid?: string;
    name: string;
    notes: string;
    review: boolean;
    template?: string;
    chat_support: boolean;
    instant_fit_subscriber: boolean;
    consent_forms: boolean;
    callback_config?: {};
    client_qa?: boolean;
    internal_qa?: boolean;
    cqa_autocomplete_days?: number;
    auto_run_instant_fit?: boolean;
    additional_web_search?: boolean;
    web_content_translation?: boolean;
    report_analytics_charts?: boolean;
  };
  company_settings_creation: CompanyCreationSetting[];
}

export interface DeleteCompanyBlueprintInput {
  company_uuid: string;
  blueprint_uuid: string;
}

export interface CompanyCreationResponse {
  name: string;
  external_id: string;
  is_active: boolean;
  company_settings: {
    external_id: string;
    setting_type_uuid: string;
    value: string;
  }[];
}

export interface CompanyWithBlueprintInfo {
  company_uuid: string;
  name: string;
  blueprintsInfo: BlueprintMinimalInfo[];
  client_qa?: boolean;
  internal_qa?: boolean;
  cqa_autocomplete_days?: number;
  default_behavior_kit?: string;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
}

export interface CompanySetupState {
  name: string;
  canCreateCompanies: boolean;
  canChatSupport: boolean;
  instantFitSubscriber: boolean;
  consentForms: boolean;
  showImagesInFinishReport: boolean;
  reviewCompletedReport: boolean;
  notes: string;
  loaded?: boolean;
  template?: string;
  clientQa?: boolean;
  internalQa?: boolean;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
}

export type CompanyBlueprintState = {
  blueprintTemplateUUID?: string;
  blueprintUUID: string;
  behavior: BehaviorInfoMinimal[];
  lookbackPeriod: number;
  sla: number;
  isTemplate?: boolean;
};

export enum SETTING_TYPES_ENUM {
  LOOK_BACK_PERIOD = "LOOK_BACK_PERIOD",
  SHOW_IMAGE_IN_REPORT = "SHOW_IMAGE_IN_REPORT",
  USER_CREATION_LIMIT = "USER_CREATION_LIMIT",
  SUBCOMPANY_CREATION_LIMIT = "SUBCOMPANY_CREATION_LIMIT",
  BLUEPRINT_CREATION_LIMIT = "BLUEPRINT_CREATION_LIMIT",
  CAN_CREATE_COMPANY = "CAN_CREATE_COMPANY",
  PDF_TEMPLATE = "PDF_TEMPLATE",
  HAS_CUSTOM_PDF_TEMPLATE = "HAS_CUSTOM_PDF_TEMPLATE",
}

export interface SettingType {
  external_id: string;
  default: string;
  name: string;
  data_type: string;
  description: string;
}

export interface PDFTemplate {
  option: string;
  logo_url: string;
  body: string;
  footer: string;
}

export interface CompanyEditHeaders {
  [key: string]: string;
}
export interface CompanySettingsInfo {
  company_setting_uuid: string;
  setting_type_uuid: string;
  setting_name: string;
  setting_value: string | boolean;
}

export interface CompanyUpdateInput {
  companyUUID: string;
  updates: Map<string, any>;
}

export interface CompanyEx {
  settings: any;
  blueprints: any;
  behavior_kits: any;
  parent_company_id?: number;
  name: string;
  notes: string;
  banner_notes?: string;
  review: boolean;
  external_id: string;
  template?: string;
  chat_support: boolean;
  instant_fit_subscriber: boolean;
  consent_forms: boolean;
  callback_config: any;
  template_definition?: PDFTemplate;
  client_qa?: boolean;
  internal_qa?: boolean;
  cqa_autocomplete_days?: number;
  default_behavior_kit?: string;
  default_behavior_kit_assigned_by?: string;
  default_behavior_kit_assigned_by_name?: string;
  default_blueprint?: string;
  auto_run_instant_fit?: boolean;
  additional_web_search?: boolean;
  web_content_translation?: boolean;
  multilanguage?: boolean;
  lens_topics?: boolean;
  show_person_info_on_pdf?: boolean;
  callback_on_amendment_complete?: boolean;
  data_residency?: DataResidency | null;
}

export type DataResidency = "US" | "EU";

export interface BehaviorKitUpdate {
  external_id?: string;
  company_uuid: string;
  lookback?: number;
  name: string;
  display_images: boolean;
  behaviors: string[];
  modified_by?: string;
}

export interface BehaviorKit {
  external_id?: string;
  company_uuid: string;
  lookback?: number;
  name: string;
  display_images: boolean;
  modified_by?: string;
}

export interface Behavior {
  name: string;
}

export interface CompanyLogoInput {
  file: File;
  companyUUID: string;
}

export interface AdverseFlagDistribution {
  [key: string]: number;
}

export interface AdverseFlagStats {
  max: number;
  min: number;
  median: number;
  avg: number;
  percentile_10: number;
  percentile_20: number;
  percentile_30: number;
  percentile_40: number;
  percentile_50: number;
  percentile_60: number;
  percentile_70: number;
  percentile_80: number;
  percentile_85: number;
  percentile_90: number;
  percentile_95: number;
  percentile_99: number;
  percentile_99_99: number;
  adverse_flag_cutoff?: number;
}

export interface CandidateInfo {
  flag_count: number;
  percentile: number;
  total_candidates: number;
}

export interface DistributionPoint {
  flag_count: number;
  candidate_count: number;
  cumulative_percent: number;
}

export interface ChartReadyData {
  percentile_line_position: number;
  highlight_flag_value: number;
  distribution_points: DistributionPoint[];
}

export interface CandidateComparison {
  count: number;
  percentage: number;
}

export interface Comparison {
  candidates_with_fewer: CandidateComparison;
  candidates_with_more: CandidateComparison;
  candidates_with_same: CandidateComparison;
}

export interface ReportMetadata {
  end_date: string;
  start_date: string;
}

export interface AnalyticsData {
  adverse_flag_dist: AdverseFlagDistribution;
  adverse_flag_stats: AdverseFlagStats;
  candidate: CandidateInfo;
  chart_ready_data: ChartReadyData;
  company_name: string;
  company_uuid: string;
  comparison: Comparison;
  metadata: ReportMetadata;
  report_total: number;
}

export interface CompanyMetricsRequest {
  company_uuid: string;
  candidate_flags: number;
}

export interface ReportsLookupRequest {
  company_uuids: string[];
  start_date: string;
  finish_date: string;
  page?: number;
  page_size?: number;
  /** When false (default), Item DAL runs a count query for total/pages. */
  skip_count?: boolean;
}

/** Matches CSV export: underscores → spaces, uppercased (e.g. IN PROGRESS, DONE). */
export function formatReportsLookupStatus(
  raw: string | null | undefined
): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.replace(/_/g, " ").toUpperCase();
}

export interface ReportsLookupRecord {
  created_at: string;
  delivered_at: string;
  report_uuid: string;
  company_name: string;
  finished_at: string;
  started_at: string;
  person_name: string;
  blueprint_name: string;
  behavior_kit_name: string;
  status: string;
  requested_by: string;
}

export interface ReportsLookupResponse {
  records: ReportsLookupRecord[];
  page: number;
  pages: number;
  size: number;
  total: number;
}

export interface ReportsLookupExportRequest {
  company_uuids: string[];
  start_date: string;
  finish_date: string;
  /** When true (default), skips count query on each export page for faster CSV builds. */
  skip_count?: boolean;
}
